import classNames from 'classnames'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap'
import documents from '../../api/documents/hooks'
import { DocumentFile } from '../../api/documents/types'
import { MODAL_TYPES } from '../../common/constants'
import { action } from '../AppPermissionsSwitcher/constants/actions'
import { setupDeleteCallback } from '../Modals/DeleteModal/utils'
import { showModal } from '../../store/modal'
import { selectSearchCriteriaDocuments } from '../../store/search'
import { selectCheckedTableRows } from '../../store/table'
import { DataTableLayout, useDataTable, useRowPermission, useTableState } from '../TanstackTable'
import DocumentEditPanel from './DocumentEditPanel'
import { useTableColumns } from './hooks'

export interface DocumentTableProps {
    parentId?: string | number
    documentModule?: string
    contactType?: string
    onAddClick?: () => void
}

const DocumentTable = ({ parentId, documentModule, contactType, onAddClick }: DocumentTableProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [documentData, setDocumentData] = useState<DocumentFile | undefined>(undefined)

    const documentSearchCriteria = useSelector((state) => (!parentId ? selectSearchCriteriaDocuments(state) : undefined))
    const selectedTableRows = useSelector(selectCheckedTableRows)

    const tableState = useTableState({ tableKey: 'documents' })

    const queryParams = {
        ...tableState.params,
        collection: 'document',
        ...(documentModule ? { module: documentModule } : {}),
        ...(documentSearchCriteria ? { criteria: documentSearchCriteria } : {}),
    }

    const { isLoading: fetchingDocuments, data: documentsData } = documents.GetDocuments.useQuery(parentId, queryParams)
    const { mutate: deleteDocuments } = documents.DeleteMultipleDocuments.useMutation()
    const { mutate: syncDocument } = documents.SyncDocument.useMutation()

    const sidebarClose = () => setDocumentData(undefined)

    const handleDelete = () => {
        const callbackId = 'delete-documents'
        const selectedRowsIds = selectedTableRows.map((row: { id: number }) => row.id)

        const payload = {
            modalType: MODAL_TYPES.DELETE_MODAL,
            modalProps: { callbackId, selectedRowsIds },
        }

        setupDeleteCallback(payload.modalType, payload.modalProps, (ids) => deleteDocuments(ids as number[]))
        dispatch(showModal(payload))
    }

    const columns = useTableColumns(documentModule, syncDocument, setDocumentData)
    const enableRow = useRowPermission<DocumentFile>('documents', action.delete)

    const { table, updateColumnSizing } = useDataTable({
        ...tableState,
        columns,
        data: documentsData?.items ?? [],
        pageCount: documentsData?.pagination?.totalPages,
        selectRow: {
            mapRow: (row: DocumentFile) => ({ id: row.id }),
            enableRow,
        },
    })

    const tableActions = () => (
        <div className="d-flex flex-grow-1">
            {onAddClick && (
                <Button
                    onClick={(e) => {
                        e.preventDefault()
                        onAddClick()
                    }}
                    color="add"
                    className="btn-label"
                >
                    <i className="mdi mdi-plus align-bottom label-icon align-middle fs-16 me-2"></i>
                    {t('button.add')}
                </Button>
            )}

            <UncontrolledDropdown className="ms-2">
                <DropdownToggle title={t('table.showMore')} href="#" className="btn btn-primary dropdown" tag="button">
                    <i className="mdi mdi-dots-horizontal align-middle"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem onClick={handleDelete} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i> {t('table.action.deleteSelected')}
                    </DropdownItem>
                </DropdownMenu>
            </UncontrolledDropdown>
        </div>
    )

    return (
        <div className="d-lg-flex gap-1 position-relative">
            <div className={classNames('file-manager-content w-100 px-0 py-0', { 'pe-2': documentData })}>
                <DataTableLayout
                    table={table}
                    isLoading={fetchingDocuments}
                    totalItems={documentsData?.pagination?.totalItems}
                    theadClass="dmn-table-header"
                    toolbarActions={tableActions}
                    updateColumnSizing={updateColumnSizing}
                />
            </div>
            {documentData && (
                <DocumentEditPanel documentData={documentData} sidebarClose={sidebarClose} contactType={contactType} parentId={parentId} />
            )}
        </div>
    )
}

export default DocumentTable
